import { v4 as uuidv4 }         from 'uuid';
import { helper as $helper }    from '@/utils/helper';
import { createPath }           from '@/fabric/objects.js';

import {
    canvas,
    layout,
    canvasIds,
    currentObjects,
    currentBackground,
    removeElement,
    clearCanvasEvents,
    disableCanvasActions,
    updateCanvasIds
} from '@/fabric/global.js';

let polygonPoints = [];
let tempShapes = [];

function finishPolygon(opt, onKeyDown) {
    if (polygonPoints.length < 3) {
        tempShapes.forEach(shape => canvas.remove(shape));
        tempShapes = [];
        polygonPoints = [];
        window.removeEventListener('keydown', onKeyDown);
        return;
    }

    const polygon = new fabric.Polygon(polygonPoints, {
        uuid:           uuidv4(),
        uuid_parent:    getCanvasId(opt.tagId),
        id:             opt.tagId,
        name:           opt.tagId,
        color:          opt.rawColor,
        stroke:         null,
        fill:           opt.rawColor,
        strokeWidth:    0,
        opacity:        opt.opacity,
        elementType:    'mask',
        erasable:       true,
        strokeUniform:  true,
        evented:        true,
        selectable:     false,
        paintFirst:     'fill',
        strokeLineCap:  'round',
        strokeLineJoin: 'round',
    });

    const backgroundObj = currentBackground.value;
    if (backgroundObj) {
        const clipRect = new fabric.Rect({
            left:               backgroundObj.left,
            top:                backgroundObj.top,
            width:              backgroundObj.width,
            height:             backgroundObj.height,
            absolutePositioned: true
        });
        polygon.clipPath = clipRect;
    }
    
    canvas.add(polygon);
    canvas.bringToFront(polygon);

    tempShapes.forEach(shape => canvas.remove(shape));
    tempShapes = [];
    polygonPoints = [];

    layout.value.status.multilabelTag = layout.value.dataset.tags['0'];
    
    canvas._historyRecording = true;
    canvas._pushHistory();

    window.removeEventListener('keydown', onKeyDown);
    window.addEventListener('keydown', onKeyDown);
    canvas.renderAll();
}

let currentPolygonOpt = {};
let onKeyDownHandler = null;
let onMouseDownHandler = null;

export function setupPolygonDrawing(opt = {}) {
    clearCanvasEvents();
    disableCanvasActions();
    canvas.isDrawingMode = false;
    canvas.selection = false;

    currentPolygonOpt = opt;
    polygonPoints = [];
    tempShapes = [];
    
    canvas._historyRecording = false;

    onMouseDownHandler = function(o) {
        const pointer = canvas.getPointer(o.e);

        if (polygonPoints.length > 0) {
            const firstPoint = polygonPoints[0];
            const distance = Math.sqrt(Math.pow(pointer.x - firstPoint.x, 2) + Math.pow(pointer.y - firstPoint.y, 2));

            if (distance < 10 && polygonPoints.length > 2) {
                finishPolygon(currentPolygonOpt, onKeyDownHandler);
                return;
            }
        }
        
        polygonPoints.push(pointer);

        const pointCircle = new fabric.Circle({
            radius: 6,
            fill: currentPolygonOpt.rawColor,
            left: pointer.x,
            top: pointer.y,
            originX: 'center',
            originY: 'center',
            selectable: false,
            evented: false,
            isPolygonTemp: true,
            excludeFromExport: true,
        });
        canvas.add(pointCircle);
        tempShapes.push(pointCircle);

        if (polygonPoints.length > 1) {
            const prevPoint = polygonPoints[polygonPoints.length - 2];
            const line = new fabric.Line([prevPoint.x, prevPoint.y, pointer.x, pointer.y], {
                stroke: currentPolygonOpt.rawColor,
                strokeWidth: 2,
                selectable: false,
                evented: false,
                isPolygonTemp: true,
                excludeFromExport: true,
            });
            canvas.add(line);
            tempShapes.push(line);
        }
        canvas.renderAll();
    };

    onKeyDownHandler = function(e) {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            if (polygonPoints.length > 2) {
                finishPolygon(currentPolygonOpt, onKeyDownHandler);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            
            const currentObjects = canvas.getObjects();
            const tempObjectsToRemove = currentObjects.filter(obj => obj.isPolygonTemp === true);
            tempObjectsToRemove.forEach(obj => canvas.remove(obj));
            
            tempShapes = [];
            polygonPoints = [];
            canvas._historyRecording = false;
            canvas.renderAll();
        }
    };
    
    canvas.on('mouse:down', onMouseDownHandler);
    window.addEventListener('keydown', onKeyDownHandler);

    canvas.defaultCursor = 'crosshair';
}

export function setupDrawingPath (opt = {}) {
    cleanupPolygonDrawing();
    clearCanvasEvents();
    disableCanvasActions();
    
    canvas.isDrawingMode            = true;
    canvas.freeDrawingBrush         = new fabric.PencilBrush(canvas);
    canvas.freeDrawingBrush.width   = opt.cursorSize;
    canvas.freeDrawingBrush.color   = opt.cursorColor;

    updateCursor(opt.cursorSize, opt.cursorColor);

    const backgroundObj = currentBackground.value;
    handleSegmentationLimits(backgroundObj);
    
    const originalOnMouseMove = canvas.freeDrawingBrush.onMouseMove;
    canvas.freeDrawingBrush.onMouseMove = function(pointer, options) {
        if (backgroundObj) {
            const halfBrushWidth = opt.cursorSize / 2;
            pointer.x = Math.max(
                backgroundObj.left + halfBrushWidth, 
                Math.min(backgroundObj.left + backgroundObj.width - halfBrushWidth, pointer.x)
            );
            pointer.y = Math.max(
                backgroundObj.top + halfBrushWidth, 
                Math.min(backgroundObj.top + backgroundObj.height - halfBrushWidth, pointer.y)
            );
        }
        originalOnMouseMove.call(this, pointer, options);
    };
    
    const originalAddPoint = canvas.freeDrawingBrush._addPoint;
    canvas.freeDrawingBrush._addPoint = function(point) {
        if (backgroundObj) {
            const halfBrushWidth = opt.cursorSize / 2;
            point.x = Math.max(
                backgroundObj.left + halfBrushWidth, 
                Math.min(backgroundObj.left + backgroundObj.width - halfBrushWidth, point.x)
            );
            point.y = Math.max(
                backgroundObj.top + halfBrushWidth, 
                Math.min(backgroundObj.top + backgroundObj.height - halfBrushWidth, point.y)
            );
        }
        return originalAddPoint.call(this, point);
    };

    canvas.freeDrawingBrush.createPath = function(pathData) {
        layout.value.status.multilabelTag = layout.value.dataset.tags['0'];

        const pathOpt = {
            id:             opt.tagId,
            name:           opt.tagId,
            color:          opt.rawColor,
            stroke:         opt.rawColor,
            opacity:        layout.value.canvas.filters.opacity,
            strokeWidth:    opt.cursorSize,
        }

        let path = createPath(pathData, pathOpt);
        path.uuid_parent = getCanvasId(opt.tagId);

        if (backgroundObj) {
            const clipRect = new fabric.Rect({
                left:               backgroundObj.left,
                top:                backgroundObj.top,
                width:              backgroundObj.width,
                height:             backgroundObj.height,
                absolutePositioned: true
            });
            
            path.clipPath = clipRect;
        }

        canvas.bringToFront(path);
        
        if (canvas._renderTimeout) {
            clearTimeout(canvas._renderTimeout);
        }
        canvas._renderTimeout = setTimeout(() => {
        canvas.renderAll();
        }, 16);

        return path;
    }

    canvas.on('path:created', function(e) {
        layout.value._tempDrawingObject = e.path;
        
        if (canvas._pathConfirmHandler) {
            window.removeEventListener('keydown', canvas._pathConfirmHandler);
            window.removeEventListener('mousedown', canvas._pathConfirmHandler);
        }
        
        const confirmObject = (event) => {
            if (event.key === "Escape") {
                if (layout.value._tempDrawingObject) {
                    removeElement(layout.value._tempDrawingObject.uuid);
                    layout.value._tempDrawingObject = null;
                    canvas.renderAll();
                }
            }
            
            if (layout.value._tempDrawingObject) {
                layout.value._tempDrawingObject.isTemporary = false;
                layout.value._tempDrawingObject = null;

                canvas._historyRecording = true;
                canvas._pushHistory();

                canvas.renderAll();
            }
            
            window.removeEventListener('keydown', canvas._pathConfirmHandler);
            window.removeEventListener('mousedown', canvas._pathConfirmHandler);
            canvas._pathConfirmHandler = null;
        };
        
        canvas._pathConfirmHandler = confirmObject;
        window.addEventListener('keydown', confirmObject);
        window.addEventListener('mousedown', confirmObject);
        
        canvas.renderAll();
    });

    canvas.on('mouse:out', function() {
        canvas.defaultCursor = "default";
    });
}

export function setupEraserPath (opt = {}) {
    cleanupPolygonDrawing();
    clearCanvasEvents();

    canvas.isDrawingMode             = true;
    canvas.preserveObjectStacking    = true;
    canvas.freeDrawingBrush          = new fabric.EraserBrush(canvas);
    canvas.freeDrawingBrush.width    = opt.cursorSize;
    canvas.freeDrawingBrush.color    = opt.cursorColor;
    canvas.freeDrawingBrush.id       = 'erasure';
    canvas.freeDrawingBrush.globalCompositeOperation = 'destination-out';

    updateCursor(opt.cursorSize, opt.cursorColor);
    
    const backgroundObj = canvas.getObjects().find(obj => obj.elementType === 'background');
    if (!backgroundObj) return;
    
    handleSegmentationLimits(backgroundObj);
    
    const originalOnMouseMove = canvas.freeDrawingBrush.onMouseMove;
    canvas.freeDrawingBrush.onMouseMove = function(pointer, options) {
        if (backgroundObj) {
            const halfBrushWidth = opt.cursorSize / 2;
            
            pointer.x = Math.max(
                backgroundObj.left + halfBrushWidth, 
                Math.min(backgroundObj.left + backgroundObj.width - halfBrushWidth, pointer.x)
            );
            pointer.y = Math.max(
                backgroundObj.top + halfBrushWidth, 
                Math.min(backgroundObj.top + backgroundObj.height - halfBrushWidth, pointer.y)
            );
        }
        originalOnMouseMove.call(this, pointer, options);
    };
    
    const originalAddPoint = canvas.freeDrawingBrush._addPoint;
    canvas.freeDrawingBrush._addPoint = function(point) {
        if (backgroundObj) {
            const halfBrushWidth = opt.cursorSize / 2;
            
            point.x = Math.max(
                backgroundObj.left + halfBrushWidth, 
                Math.min(backgroundObj.left + backgroundObj.width - halfBrushWidth, point.x)
            );
            point.y = Math.max(
                backgroundObj.top + halfBrushWidth, 
                Math.min(backgroundObj.top + backgroundObj.height - halfBrushWidth, point.y)
            );
        }
        return originalAddPoint.call(this, point);
    };

    canvas.on('path:created', async function(e) {
        e.path.globalCompositeOperation = 'destination-out';
        
        if (backgroundObj) {
            const clipRect = new fabric.Rect({
                left:               backgroundObj.left,
                top:                backgroundObj.top,
                width:              backgroundObj.width,
                height:             backgroundObj.height,
                absolutePositioned: true
            });
            
            e.path.clipPath = clipRect;
        }
        
        canvas._historyRecording = false;
        
        await checkDeletedPaths();
        
        canvas.remove(e.path);
        
        canvas._historyRecording = true;
        canvas._pushHistory();
        
        if (canvas._renderTimeout) {
            clearTimeout(canvas._renderTimeout);
        }
        canvas._renderTimeout = setTimeout(() => {
        canvas.renderAll();
        }, 16);
    });

    canvas.on('mouse:out', function() {
        canvas.defaultCursor = "default";
    });
}

export function getCanvasId (tagId) {
    const canvasFilter = canvasIds.value.find(canvasObj => canvasObj.name === tagId);
    if (canvasFilter) {
        return canvasFilter.uuid;
    } else {
        const newCanvasObj = {
            uuid:   uuidv4(),
            index:  1,
            name:   tagId,
            stroke: layout.value.dataset.tags[tagId].color,
            type:   "path"
        };

        canvasIds.value.push(newCanvasObj);
        return newCanvasObj.uuid;
    }
}

export function updateSelectedCanvas (event, canvasId) {
    if (!layout.value.selectedCanvas && !canvasId) return;

    const currentId     = canvasId || layout.value.selectedCanvas.uuid;
    const currentTagId  = event?.target?.value || layout.value.status.currentTag.id;

    let canvasObj = canvasIds.value.find(canvas => canvas.uuid === currentId);
    if (canvasObj) {
        const currentColor = layout.value.dataset.tags[currentTagId].color;

        canvasObj.id        = currentTagId;
        canvasObj.name      = currentTagId;
        canvasObj.stroke    = currentColor;

        currentObjects.value.filter(obj => obj.uuid_parent === currentId).forEach(obj => {
            obj.set({
                id:     currentTagId,
                name:   currentTagId,
                color:  currentColor,
                stroke: currentColor
            }).setCoords();

            if (obj.getElement && typeof obj.getElement === 'function') {
                const imgObject = obj.getElement();
                if (imgObject) {
                    const tempCanvas = document.createElement('canvas');
                    const context = tempCanvas.getContext('2d');
                    tempCanvas.width = obj.width;
                    tempCanvas.height = obj.height;

                    context.drawImage(imgObject, 0, 0, obj.width, obj.height);
                    const imageData = context.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
                    const data = imageData.data;
                    const { r, g, b } = $helper.convertHex(obj.stroke);

                    for (let i = 0; i < data.length; i += 4) {
                        if (data[i + 3] > 0) {
                            data[i] = r;
                            data[i + 1] = g;
                            data[i + 2] = b;
                        }
                    }

                    context.putImageData(imageData, 0, 0);
                    obj.setElement(tempCanvas, () => { 
                        obj.canvas.renderAll();
                        tempCanvas.width = 0;
                        tempCanvas.height = 0;
                    });
                    obj.setCoords();
                }
            }
        });
    }
    
    updateCanvasIds();
    canvas._pushHistory();
    canvas.renderAll();
}

function updateCursor(size = false, color = false) {
    let limitedSize = Math.min(size * canvas.getZoom(), 120);
  
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${ limitedSize }" height="${ limitedSize }">
          <circle cx="${ limitedSize / 2 }" cy="${ limitedSize / 2 }" r="${ limitedSize / 2 }" fill="${ color }"/>
      </svg>
    `;
  
    canvas.freeDrawingCursor = `url('data:image/svg+xml;utf8,${ encodeURIComponent(svg) }') ${ limitedSize / 2 } ${ limitedSize / 2 }, auto`;
    canvas.renderAll();
}

function handleSegmentationLimits (backgroundObj) {
    canvas.on('mouse:down', function(options) {
        const pointer = canvas.getPointer(options.e);
        const isOutsideImage = (
            pointer.x < backgroundObj.left || 
            pointer.x > backgroundObj.left + backgroundObj.width ||
            pointer.y < backgroundObj.top || 
            pointer.y > backgroundObj.top + backgroundObj.height
        );
        
        if (isOutsideImage) {
            options.e.stopPropagation();
            options.e.preventDefault();
            return false;
        }
    }, { passive: false });
}

async function checkDeletedPaths () {
    const currentObjects = canvas.getObjects();

    for (let obj of currentObjects) {
        if (obj.elementType === 'background') continue;

        let img = new Image();
        img.onload = function() {
            const sampleSize = Math.min(100, Math.max(obj.width || 100, obj.height || 100));
            let tempCanvas = document.createElement('canvas');
            tempCanvas.width = sampleSize;
            tempCanvas.height = sampleSize;

            let ctx = tempCanvas.getContext('2d');
            ctx.drawImage(img, 0, 0, sampleSize, sampleSize);

            let imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
            let data = imageData.data;

            let esTransparente = true;
            for (let i = 3; i < data.length; i += 16) {
                if (data[i] > 10) {
                    esTransparente = false;
                    break;
                }
            }

            if (esTransparente) {
                removeElement(obj.uuid);
            }

            tempCanvas.width = 0;
            tempCanvas.height = 0;
        };

        img.src = obj.toDataURL();
    }
}

export function cleanupPolygonDrawing() {
    tempShapes.forEach(shape => canvas.remove(shape));
    tempShapes = [];
    polygonPoints = [];
    
    if (onKeyDownHandler) {
        window.removeEventListener('keydown', onKeyDownHandler);
        onKeyDownHandler = null;
    }
    
    if (onMouseDownHandler) {
        canvas.off('mouse:down', onMouseDownHandler);
        onMouseDownHandler = null;
    }
    
    canvas._historyRecording = true;
    canvas.defaultCursor = 'default';
}

export function cleanupSegmentationResources() {
    cleanupPolygonDrawing();
    
    if (canvas._renderTimeout) {
        clearTimeout(canvas._renderTimeout);
        canvas._renderTimeout = null;
    }
    
    if (canvas._pathConfirmHandler) {
        window.removeEventListener('keydown', canvas._pathConfirmHandler);
        window.removeEventListener('mousedown', canvas._pathConfirmHandler);
        canvas._pathConfirmHandler = null;
    }
    
    if (window.gc && typeof window.gc === 'function') {
        window.gc();
    }
}